import { SHIPPING_METHOD_MAP_NUMBER } from '@/constants/orderConstants';
import type { Request } from '@/types/requests';
import { formatDate } from '@/utils';

/**
 * Order status code to readable label mapping
 */
const ORDER_STATUS_MAP: Record<number, string> = {
	953870000: 'Pending Shipment', // Open
	953870001: 'Shipped', // Shipped
	953870002: 'Denied', // Order Denied
	953870003: 'Pending Shipment', // Ready To Ship
	953870004: 'Pending Shipment', // Sent to FB
	953870005: 'Partial Shipment',
	953870006: 'Pending Shipment', // Submitted to Manufacturer
};

/**
 * Get readable FedEx shipping method label
 */
export const getFedExShippingMethodLabel = (code?: number): string => {
	if (!code) return '-';
	return SHIPPING_METHOD_MAP_NUMBER[code] || `Unknown (${code})`;
};

/**
 * Get readable order status label
 */
export const getOrderStatusLabel = (code?: number): string => {
	if (!code) return '-';
	return ORDER_STATUS_MAP[code] || `Unknown Status (${code})`;
};

/**
 * Determine order type based on BV ID
 * Request orders have a BV ID, Bulk orders don't
 */
const getOrderType = (order: Request): string => {
	return (order?.ta_consignmenttype && order?.ta_consignmenttype === 953870001) ? 'Truck Stock' : order?._ta_bvid_value ? 'Individual' : 'Bulk';
};

/**
 * Build complete shipping location string (name + address + city, state)
 */
export const buildShippingLocationString = (order: Request): string => {
	const location = order.ta_ShippingLocation;
	if (!location) return '-';

	const parts = [
		location.ta_name,
		location.ta_addressline,
		location.ta_citylocality && location.ta_stateprovince
			? `${location.ta_citylocality}, ${location.ta_stateprovince}`
			: location.ta_citylocality || location.ta_stateprovince,
	];

	return parts.filter(Boolean).join(', ') || '-';
};

/**
 * Build array of product strings for bulk orders (for tile/badge display in DetailsView)
 * Each item: "name (size) (qty)"
 */
const buildBulkProductsArray = (orders: Request[]): string[] => {
	if (!orders?.length) return [];
	return orders.map((order) => {
		const name = order.ta_SKU?.ta_name ?? '-';
		const size = order.ta_SKU?.ta_size ?? '-';
		const qty = order.ta_quantity?.toString() ?? '-';
		return `${name} (${size}) (${qty})`;
	});
};

/**
 * Parse bulk order data into DetailsView format
 * Shows shared order info and a single "Products" field with all products as comma-separated: ta_sku, name (size) (qty)
 */
export const parseBulkOrderDetailsForModal = (orders: Request[]) => {
	if (!orders?.length) return null;
	const order = orders[0];
	const orderType = getOrderType(order);
	const returnsData: Array<{
		label: string;
		value: string;
		navigateTo?: string;
		linkText?: string;
	}> = [];

	if (
		order.returnRequests &&
		Array.isArray(order.returnRequests) &&
		order.returnRequests.length > 0
	) {
		order.returnRequests.forEach((returnItem, index) => {
			if (returnItem.ta_returnrequestid && returnItem.ta_name) {
				returnsData.push({
					label: `Return ${index > 0 ? index : ''}`,
					value: returnItem.ta_name,
					navigateTo: `/returns?returnId=${returnItem.ta_returnrequestid}`,
					linkText: returnItem.ta_name,
				});
			}
		});
	}

	return [
		// Column 1: Order & Clinic Information
		{
			data: [
				{ label: 'Orders Type', value: orderType },
				{ label: 'Provider', value: order?.ta_Doctor?.fullname || '-' },
				{ label: 'Clinic', value: order?.ta_Clinic?.name || '-' },
				{ label: 'Status', value: getOrderStatusLabel(order.ta_status) },
				{ label: 'Request Date', value: formatDate(order.ta_orderrequestdate) },
				{ label: 'Date of Service', value: formatDate(order.ta_dos) },
				{ label: 'Clinic PO', value: order.ta_clinicpo || '-' },
				...returnsData,
			],
		},
		// Column 2: Products (tiles/badges like BV) & Shipping Information
		{
			data: [
				{ label: 'Products', value: buildBulkProductsArray(orders) },
				{
					label: 'Shipping Location',
					value: buildShippingLocationString(order),
				},
				{
					label: 'Shipping Method',
					value: getFedExShippingMethodLabel(order.ta_fedexshippingmethod),
				},
				{ label: 'Comments', value: order.ta_comments || '-' },
			],
		},
	];
};

/**
 * Parse order data into DetailsView format
 * Transforms raw order data into 2-column structured display
 */
export const parseOrderDetailsForModal = (order: Request) => {
	const orderType = getOrderType(order);
	const isRequestType = orderType === 'Individual';
	const isBulkType = !isRequestType;

	// Process returns data - create clickable links for each return
	const returnsData: Array<{
		label: string;
		value: string;
		navigateTo?: string;
		linkText?: string;
	}> = [];

	if (
		order.returnRequests &&
		Array.isArray(order.returnRequests) &&
		order.returnRequests.length > 0
	) {
		order.returnRequests.forEach((returnItem, index) => {
			if (returnItem.ta_returnrequestid && returnItem.ta_name) {
				returnsData.push({
					label: `Return ${index > 0 ? index : ''}`,
					value: returnItem.ta_name,
					navigateTo: `/returns?returnId=${returnItem.ta_returnrequestid}`,
					linkText: returnItem.ta_name,
				});
			}
		});
	}

	return [
		// Column 1: Order & Clinic Information
		{
			data: [
				{ label: 'Orders Type', value: orderType },
				{ label: 'Provider', value: order?.ta_Doctor?.fullname || '-' },
				// Only show Patient for Request type orders (Individual)
				...(isRequestType
					? [{ label: 'Patient', value: order?.ta_BVPatient?.fullname || '-' }]
					: []),
				{ label: 'Clinic', value: order?.ta_Clinic?.name || '-' },

				{ label: 'Status', value: getOrderStatusLabel(order.ta_status) },
				{ label: 'Request Date', value: formatDate(order.ta_orderrequestdate) },
				{ label: 'Date of Service', value: formatDate(order.ta_dos) },
				// Only show Clinic PO for Bulk orders
				...(isBulkType
					? [{ label: 'Clinic PO', value: order.ta_clinicpo || '-' }]
					: []),
				// Add returns if they exist
				...returnsData,
			],
		},
		// Column 2: Product & Shipping Information
		{
			data: [
				{ label: 'Product', value: order.ta_SKU?.ta_name || '-' },
				{ label: 'Quantity', value: order.ta_quantity?.toString() || '-' },
				{ label: 'Size', value: order.ta_SKU?.ta_size || '-' },
				{
					label: 'Shipping Location',
					value: buildShippingLocationString(order),
				},
				{
					label: 'Shipping Method',
					value: getFedExShippingMethodLabel(order.ta_fedexshippingmethod),
				},
				...(order?.shipments?.length
				? (() => {
						const latestShipment = [...order.shipments]
							.filter((s) => s?.createdon)
							.sort(
								(a, b) =>
									new Date(b.createdon || '').getTime() - new Date(a.createdon || '').getTime()
							)?.[0];

						return latestShipment?.ta_trackingnumber
							? [
									{
										label: 'Tracking Number',
										value: latestShipment.ta_trackingnumber,
										link: `https://www.fedex.com/wtrk/track/?trknbr=${latestShipment.ta_trackingnumber}`,
									},
								]
							: [];
					})()
				: []),
				{ label: 'Comments', value: order.ta_comments || '-' },
			],
		},
	];
};
